import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { api } from './api';
import PayeeInfo, { type Payee } from './PayeeInfo';
import { decimalInput, money } from './ui';

export const PAY_METHODS: Record<string, string> = {
  BANK: '对公转账',
  WECHAT: '微信',
  ALIPAY: '支付宝',
  CASH: '现金',
  OTHER: '其他',
};

interface Props {
  /** Where the declaration is posted: an order's or a statement's payment-claims. */
  path: string;
  peer: string;
  /** Still payable: the locked amount minus confirmed and pending declarations. */
  left: number;
  payee: Payee | null;
  onClose: () => void;
  onDone: () => void;
}

/** The buyer pays outside the platform, then declares it here for the supplier to confirm. */
export default function PayDialog(p: Props) {
  const [amount, setAmount] = useState(p.left.toFixed(2));
  const [method, setMethod] = useState(
    p.payee?.bankAccount ? 'BANK' : p.payee?.hasWechatQr ? 'WECHAT' : 'BANK',
  );
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => crypto.randomUUID());

  async function submit() {
    if (busy) return;
    if (!(Number(amount) > 0)) return setError('请填写本次付款金额');
    setBusy(true);
    setError('');
    try {
      await api(p.path, { amount, method, reference, key });
      p.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>
        向{p.peer}付款
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          通过下方账户或收款码直接付给对方，平台不经手资金。付款后提交，由对方确认收款。
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              应付金额
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700 }}>{money(p.left)}</Typography>
          </Box>
          <PayeeInfo payee={p.payee} />
          <Box sx={{ borderTop: '1px solid #edf1f2', pt: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 650, mb: 1.5 }}>付款后填写</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="本次付款金额"
                value={amount}
                disabled={busy}
                onChange={(e) => setAmount(decimalInput(e.target.value, 2))}
                slotProps={{
                  input: { startAdornment: <InputAdornment position="start">¥</InputAdornment> },
                }}
              />
              <TextField
                select
                label="付款方式"
                value={method}
                disabled={busy}
                onChange={(e) => setMethod(e.target.value)}
              >
                {Object.entries(PAY_METHODS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="流水号 / 备注（选填）"
                placeholder="便于对方核对到账"
                value={reference}
                disabled={busy}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => setReference(e.target.value)}
                sx={{ gridColumn: '1 / -1' }}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button disabled={busy} onClick={p.onClose}>
          稍后再付
        </Button>
        <Button variant="contained" disabled={busy} onClick={submit}>
          {busy ? '正在提交…' : '我已付款'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
